import { getAccessStoreSingleton } from 'features/Access/infra/react/AccessState'
import { Skiptrace as Domain } from 'features/Skiptrace/domain/Skiptrace.domain'
import { mapGetSkiptraceResultResponse } from 'features/Skiptrace/infra/remote/getSkiptraceResult/getSkiptraceResult.mapper'
import { HTTPError } from 'ky'
import { restClient } from 'presentation/libs/client'

const isMatchedSkiptraceResponse = (response: any): boolean => {
  // Wallet wraps the downstream skiptrace payload in `{ balance, result }`,
  // while direct service calls return the skiptrace payload itself. Support
  // both shapes so the client classifies the result correctly.
  const skiptrace = response?.result ?? response
  const outcome = skiptrace?.result?.outcome ?? skiptrace?.outcome
  return outcome === 'match'
}

const unwrapSkiptraceResponse = (response: any) =>
  response?.result ?? response

const getCurrentTracer = async (userId?: string): Promise<Domain.Tracer[]> => {
  const accessStore = await getAccessStoreSingleton()
  const access = accessStore.getState().local.access

  if ((access.status === 'loaded' || access.status === 'updating') && access.data.status === 'logged-in') {
    return [{
      id: access.data.accessor.id,
      name: access.data.accessor.name.full || 'You',
    }]
  }

  if (!userId) return []

  return [{
    id: userId,
    name: 'You',
  }]
}

export const mapPerformedSkiptraceResponse = async (
  response: any,
  params: Pick<Domain.PerformSkiptracePayload, 'leadId' | 'address'>,
): Promise<Domain.PerformSkiptraceResult> => {
  const skiptrace = unwrapSkiptraceResponse(response)
  const tracers = await getCurrentTracer(skiptrace?.userId)
  const { result } = mapGetSkiptraceResultResponse({
    leadId: params.leadId,
    address: params.address,
    skiptraces: skiptrace ? [skiptrace] : [],
    tracers,
  })

  return {
    isSuccessful: isMatchedSkiptraceResponse(response),
    result,
  }
}

export const performSkiptrace = async (
  params: Domain.PerformSkiptracePayload,
): Promise<Domain.PerformSkiptraceResult> =>
  await restClient.post('marketing/v2/skiptrace/v4', {
    json: {
      type: 'basic',
      request: {
        line1: params.address.line1,
        city: params.address.city,
        state: params.address.state,
        zip: params.address.postalCode,
      },
      useIncludedConsumables: true,
      ...params.confirmationKey
        ? { confirmationKey: params.confirmationKey }
        : {},
    },
  })
    .json<any>()
    .then(async response => await mapPerformedSkiptraceResponse(response, params))
    .catch(error => {
      // Check if it's a 500 server error
      if (error instanceof HTTPError && error.response.status === 500) {
        // Create a specific error for 500 status that can be handled differently
        const serverError = new Error('Server error occurred during skiptrace')
        ;(serverError as any).isServerError = true
        ;(serverError as any).status = 500
        throw serverError
      }

      // Re-throw other errors as-is
      throw error
    })

export { isMatchedSkiptraceResponse }
